import { useEffect, useState } from "react";
import {
  getModuleVersionDetail,
  listModuleVersions,
  ModuleVersionDetail,
  ModuleVersionRow,
} from "@/api/methodologyConsole";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AdvancedJson,
  EmptyState,
  ErrorState,
  HashLabel,
  ImmutabilityNote,
  LifecycleBadge,
  LoadingState,
  ReasonCodes,
} from "./primitives";

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex gap-3 border-b py-1.5 text-sm last:border-0">
    <span className="w-52 shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 break-words">{value}</span>
  </div>
);

function ModuleDetail({ id }: { id: string }) {
  const [detail, setDetail] = useState<ModuleVersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDetail(null);
    getModuleVersionDetail(id)
      .then(setDetail)
      .catch((e: Error) => setError(e.message));
  }, [id]);

  if (error) return <ErrorState message={error} />;
  if (!detail) return <LoadingState label="Loading module version…" />;

  const h = detail.header as Record<string, any>;
  const blocked = Boolean(h?.publication_blocked);
  const isTowerPilot = h?.module_key === "tower_infrastructure_security" && h?.version_label?.startsWith("1.0");
  const readiness = detail.publication_readiness;

  return (
    <div className="space-y-5">
      {isTowerPilot && (
        <Alert variant="destructive">
          <AlertDescription className="font-semibold">
            INCOMPLETE PILOT VERSION — DO NOT PUBLISH — REPLACED BY VERSION 1.1
          </AlertDescription>
        </Alert>
      )}
      {blocked && (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>Publication blocked.</strong>{" "}
            {h?.authoring_metadata?.publication_blocker_reason ??
              "A governed publication blocker is set on this version."}
            {h?.authoring_metadata?.replacement_version && (
              <> Replacement: {String(h.authoring_metadata.replacement_version)}.</>
            )}
            <span className="mt-1 block text-xs">
              A blocker can only be cleared through the governed blocker procedure — never
              from a generic edit control.
            </span>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <Row label="Module" value={`${h?.module_key} — ${h?.title}`} />
        <Row label="Version" value={h?.version_label} />
        <Row
          label="Lifecycle status"
          value={
            <span className="flex items-center gap-2">
              <LifecycleBadge status={h?.status} />
              <ImmutabilityNote status={h?.status} />
            </span>
          }
        />
        <Row label="Module kind" value={h?.module_kind} />
        <Row label="Selection mode" value={h?.selection_mode ?? "—"} />
        <Row label="Questions" value={h?.question_count ?? 0} />
        <Row label="Estimated duration" value={`${h?.estimated_minutes ?? "—"} minutes`} />
        <Row label="Manifest hash" value={<HashLabel value={h?.content_hash} />} />
        <Row label="Summary" value={h?.summary ?? "—"} />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Publication readiness ({readiness?.ready ? "Ready" : "Not ready"})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ReasonCodes codes={(readiness?.reason_codes as string[]) ?? []} />
          {readiness?.ready && (
            <p className="text-sm">
              The server reports all publication prerequisites satisfied.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Validator results</CardTitle>
        </CardHeader>
        <CardContent>
          {detail.validation.length === 0 ? (
            <EmptyState
              title="No validator output"
              description="The server returned no validation checks for this module version."
            />
          ) : (
            <ul className="space-y-1 text-sm">
              {detail.validation.map((c) => (
                <li key={c.check_key} className="flex gap-2">
                  <span className={c.ok ? "text-success" : "text-destructive"}>
                    {c.ok ? "PASS" : "FAIL"}
                  </span>
                  <span className="font-medium">{c.check_key}</span>
                  <span className="text-muted-foreground">{c.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Pinned question versions ({detail.questions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {detail.questions.length === 0 ? (
            <EmptyState
              title="No pinned questions"
              description="No question version has been pinned to this module version."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Ownership</TableHead>
                  <TableHead>Pin mode</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.questions.map((q: any) => (
                  <TableRow key={String(q.question_version_id)}>
                    <TableCell className="font-medium">{q.question_key}</TableCell>
                    <TableCell>{q.version_label}</TableCell>
                    <TableCell>{q.ownership_class}</TableCell>
                    <TableCell>{q.pin_mode}</TableCell>
                    <TableCell>
                      <LifecycleBadge status={q.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Taxonomy scope</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {detail.taxonomy_scope.length === 0 ? (
              <p className="text-muted-foreground">No taxonomy scope recorded.</p>
            ) : (
              detail.taxonomy_scope.map((s: any, i) => (
                <div key={i} className="flex justify-between border-b py-1 last:border-0">
                  <span>{s.node_label}</span>
                  <span className="text-muted-foreground">
                    {s.scope_role} · {s.requirement}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Scoring, evidence & benchmarks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              Score definitions: <strong>{detail.score_definitions.length}</strong>
            </p>
            <p>
              Evidence expectations: <strong>{detail.evidence_expectations.length}</strong>
            </p>
            <p>
              Benchmark dimensions: <strong>{detail.benchmark_dimensions.length}</strong>
            </p>
            <p>
              Dependencies: <strong>{detail.dependencies.length}</strong>
            </p>
          </CardContent>
        </Card>
      </div>

      <details>
        <summary className="cursor-pointer text-sm text-muted-foreground">
          Advanced — manifest and lifecycle log
        </summary>
        <div className="mt-2 space-y-2">
          <AdvancedJson data={{ manifest: detail.manifest, lifecycle_log: detail.lifecycle_log }} />
        </div>
      </details>
    </div>
  );
}

export default function ModulesTab() {
  const [rows, setRows] = useState<ModuleVersionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    listModuleVersions()
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <ErrorState message={error} />;
  if (!rows) return <LoadingState label="Loading modules…" />;
  if (rows.length === 0)
    return (
      <EmptyState
        title="No module versions"
        description="No assessment module has been authored in this environment yet."
      />
    );

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Module</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Questions</TableHead>
            <TableHead>Manifest hash</TableHead>
            <TableHead className="text-right">Detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.module_version_id}>
              <TableCell className="font-medium">{r.title}</TableCell>
              <TableCell>{r.version_label}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.module_kind}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <LifecycleBadge status={r.status} />
                  {r.publication_blocked && <LifecycleBadge blocked />}
                </div>
              </TableCell>
              <TableCell>{r.question_count ?? 0}</TableCell>
              <TableCell>
                <HashLabel value={r.content_hash} />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOpenId(r.module_version_id)}
                >
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Module version</SheetTitle>
            <SheetDescription>
              Read-only. Lifecycle changes are performed only through governed procedures.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">{openId && <ModuleDetail id={openId} />}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
